import { bffJson, type ReadLane } from "@/shared/api/client";
import { isReadUnavailableStatus } from "@/shared/api/session-fault";
import {
  readIndividualDirectory,
  readIndividualOnce,
  settledIndividualDirectory,
} from "@/domain/clients/individual-directory";
import {
  partitionClientRows,
  type AdvisorClientRow,
  type PartitionedClientRows,
} from "@/domain/clients/client-relationship";
import { clientLinkQuery, linkOwnerCorporationId } from "@/domain/clients/individual-client";

export interface IndividualItem {
  id: string;
  fullName: string;
  personId: string;
  email: string | null;
}

const byFullName = (a: IndividualItem, b: IndividualItem) =>
  a.fullName.localeCompare(b.fullName, "sv");

type IndividualLookup =
  | { kind: "ok"; item: IndividualItem }
  | { kind: "absent"; status: number }
  | { kind: "unavailable"; status: number };

async function readIndividualById(
  individualId: string,
  lane: Pick<ReadLane, "background">,
): Promise<IndividualLookup> {
  const { status, text } = await readIndividualOnce(individualId, lane);
  if (status === 0) return { kind: "unavailable", status };
  if (status < 200 || status >= 300) {
    if (isReadUnavailableStatus(status)) {
      console.warn(
        `clients: individual read could not be performed: ${status} — nothing may be concluded about this id`,
      );
      return { kind: "unavailable", status };
    }
    console.warn(`clients: individual fetch failed: ${status}`);
    return { kind: "absent", status };
  }
  try {
    return { kind: "ok", item: toIndividualItem(JSON.parse(text) as RawIndividual) };
  } catch {
    return { kind: "unavailable", status };
  }
}

export async function fetchIndividualById(
  individualId: string,
  lane: Pick<ReadLane, "background"> = {},
): Promise<IndividualItem | null> {
  const result = await readIndividualById(individualId, lane);
  return result.kind === "ok" ? result.item : null;
}

export async function individualIsKnown(individualId: string): Promise<boolean> {
  if (settledIndividualDirectory()?.has(individualId)) return true;
  const result = await readIndividualById(individualId, {});
  if (result.kind === "unavailable") {
    throw new Error(`individual ${individualId} could not be read (${result.status})`);
  }
  return result.kind === "ok";
}

export type { AdvisorClientRow, PartitionedClientRows };

export async function fetchAdvisorClientRows(
  financialAdvisorId: string,
): Promise<PartitionedClientRows> {
  const book = { ownerCorporationId: await linkOwnerCorporationId(), financialAdvisorId };
  const [active, history] = await Promise.all([
    bffJson<unknown>(`v1/individual-client?${clientLinkQuery(book)}`),
    bffJson<unknown>(`v1/individual-client?${clientLinkQuery({ ...book, activeAt: null })}`),
  ]);
  const { live } = partitionClientRows(active);
  const liveIds = new Set(live.map((row) => row.icId));
  const ended = partitionClientRows(history).ended.filter((row) => !liveIds.has(row.icId));
  return { live, ended };
}

export async function listAdvisorIndividuals(
  rows: readonly AdvisorClientRow[],
): Promise<IndividualItem[]> {
  const found = await fetchIndividualsByIds(rows.map((row) => row.individualId));
  return [...found.values()].sort(byFullName);
}

export async function fetchIndividualsByIds(
  individualIds: readonly string[],
): Promise<ReadonlyMap<string, IndividualItem>> {
  const directory =
    individualIds.length > SINGLE_READ_LIMIT
      ? await readIndividualDirectory()
      : settledIndividualDirectory();
  const found = new Map<string, IndividualItem>();
  const missing: string[] = [];
  for (const id of new Set(individualIds)) {
    const raw = directory?.get(id);
    if (raw) found.set(id, toIndividualItem(raw as unknown as RawIndividual));
    else missing.push(id);
  }
  const read = await Promise.all(missing.map((id) => fetchIndividualById(id)));
  missing.forEach((id, index) => {
    const item = read[index];
    if (item) found.set(id, item);
  });
  return found;
}

interface RawIndividual {
  id: string;
  first_name?: string | null;
  middle_name?: string | null;
  last_name?: string | null;
  birth_date?: string | null;
  identification_numbers?: { value?: string; type?: string }[] | null;
  contact_emails?: { email_address?: string; primary?: boolean }[] | null;
}

function toIndividualItem(raw: RawIndividual): IndividualItem {
  const fullName =
    [raw.first_name, raw.middle_name, raw.last_name]
      .map((p) => (p ?? "").trim())
      .filter(Boolean)
      .join(" ") || raw.id;
  return {
    id: raw.id,
    fullName,
    personId: personnummer(raw),
    email: primaryEmail(raw),
  };
}

function personnummer(item: RawIndividual): string {
  const ids = item.identification_numbers;
  if (!Array.isArray(ids) || ids.length === 0) return "-";
  const national = ids.find((e) => e.type === "NATIONAL_IDENTIFICATION_NUMBER") ?? ids[0];
  const value = (national.value ?? "").trim();
  if (!value) return "-";
  if (/^\d{12}$/.test(value)) return `${value.slice(0, 8)}-${value.slice(8)}`;
  return value;
}

function primaryEmail(item: RawIndividual): string | null {
  const list = (item.contact_emails ?? []).filter(
    (e) => !isSeedTagEmail((e.email_address ?? "").trim()),
  );
  if (list.length === 0) return null;
  const picked = list.find((e) => e.primary) ?? list[0];
  const v = (picked?.email_address ?? "").trim();
  return v.length > 0 ? v : null;
}

function isSeedTagEmail(address: string): boolean {
  return /\.invalid$/i.test(address);
}

const SINGLE_READ_LIMIT = 4;
